import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getLesson, completeLesson, getTrainingAudioUrl } from '../utils/api';
import LessonArt from '../components/LessonArt';
import LessonHero from '../components/LessonHero';
import PracticeArena from '../components/PracticeArena';
import CprLogo from '../components/CprLogo';
import RepMascot from '../components/RepMascot';
import toast from 'react-hot-toast';

// Blank every 3rd longer word — used for practice. Display-only; the lesson text never changes.
const blankWords = (s) => { let n = 0; return s.replace(/\b([A-Za-z']{4,})\b/g, (w) => { n++; return n % 3 === 0 ? '____' : w; }); };

const PRAISE = ['Nailed it! \u2705', 'Smooth! \ud83d\udd25', 'Perfect! \u2b50', "That's it! \ud83d\udcaa", 'Closer! \ud83c\udfaf', 'Like a pro! \ud83d\ude0e'];

function Sparkles() {
  const dots = ['#c9a227', '#1a355f', '#e8c75a', '#2d4c7e', '#ffd54f', '#0d1f3b'];
  return (
    <>
      {dots.map((c, i) => {
        const a = (i / dots.length) * Math.PI * 2;
        return <span key={i} className="sparkle" style={{ right: 30, bottom: 30, background: c, '--dx': Math.cos(a) * 46 + 'px', '--dy': Math.sin(a) * 46 + 'px' }} />;
      })}
    </>
  );
}

// A quote that is an EXAMPLE of what the homeowner/customer says — NOT a rep script.
// Shown clearly, but without the recite-from-memory practice behaviour.
function ExampleQuote({ text, small }) {
  const clean = String(text).replace(/\s*\n\s*/g, ' ').trim();
  return (
    <div className="row-slide" style={{ position: 'relative', background: '#f3f5f7', borderLeft: '4px solid #90a4ae', borderRadius: 10, padding: small ? '9px 12px' : '12px 14px', margin: '10px 0', color: '#37474f' }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: '#607d8b', letterSpacing: 0.5, marginBottom: 3 }}>💬 THEY MIGHT SAY</div>
      <div style={{ fontStyle: 'italic', fontSize: small ? 14.5 : 15.5, lineHeight: 1.55 }}>{clean}</div>
    </div>
  );
}

// True when the lead-in text signals the following quotes are things the CUSTOMER says,
// e.g. "You'll hear things like:", "They'll say:", "Common objections:".
function isExampleCue(s) {
  if (!s) return false;
  return /(you'?ll hear|they'?ll say|they say|things like|homeowner|customer|objection|excuses?|might say|will say|hear (things|stuff|it)|push ?back)\b/i.test(s);
}

// Interactive "SAY THIS" script box. Tap -> practice (blanks). Tap again -> reveal + reward.
// Cycle: idle -> practice -> revealed(praise) -> idle.
function SayBox({ text, small, onPracticed }) {
  const [stage, setStage] = useState('idle');   // idle | practice | revealed
  const [praise, setPraise] = useState('');
  const clean = String(text).replace(/\s*\n\s*/g, ' ').trim();   // join wrapped lines

  function tap() {
    if (stage === 'idle') { setStage('practice'); }
    else if (stage === 'practice') {
      setStage('revealed');
      setPraise(PRAISE[Math.floor(Math.random() * PRAISE.length)]);
      if (onPracticed) onPracticed();
    } else { setStage('idle'); setPraise(''); }
  }

  const practice = stage === 'practice';
  const revealed = stage === 'revealed';
  const bg = practice ? 'linear-gradient(135deg,#fff8e1,#fffdf2)'
           : revealed ? 'linear-gradient(135deg,#e7edf7,#f3f6fb)'
           : 'linear-gradient(135deg,#eaeff6,#f0f3f7)';
  const bar = practice ? '#c9a227' : revealed ? '#2d4c7e' : '#1a355f';
  const tag = practice ? '🧠 RECITE FROM MEMORY' : revealed ? '✅ WELL DONE' : '🗣️ SAY THIS — WORD FOR WORD';
  const tagColor = practice ? '#a07f12' : '#1a355f';
  const hint = practice ? '👆 Say it out loud, then tap to reveal' : revealed ? '👆 Tap to reset' : '👆 Tap to hide words & test yourself';

  return (
    <div onClick={tap} className={'say-box' + (stage === 'idle' ? ' say-box-idle' : '')}
      style={{ position: 'relative', overflow: 'visible', background: bg, borderLeft: '5px solid ' + bar, borderRadius: 10, padding: small ? '10px 12px' : '14px 16px', paddingRight: small ? 12 : 70, margin: '12px 0', color: practice ? '#6d5505' : '#14223a', cursor: 'pointer', userSelect: 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: tagColor, letterSpacing: 0.5 }}>{tag}</span>
        <span style={{ fontSize: 10.5, fontWeight: 700, color: '#fff', background: bar, borderRadius: 20, padding: '2px 9px', whiteSpace: 'nowrap' }}>{hint}</span>
      </div>
      <div style={{ fontStyle: 'italic', fontSize: small ? 14.5 : 15.5, lineHeight: 1.55 }}>{practice ? blankWords(clean) : clean}</div>
      {revealed && <Sparkles />}
      {revealed && praise && (
        <div className="nailed-badge" style={{ position: 'absolute', right: small ? 10 : 64, top: -14, background: '#1a355f', color: '#fff', fontWeight: 800, fontSize: 13, padding: '5px 12px', borderRadius: 20, boxShadow: '0 3px 10px rgba(27,94,32,0.35)', whiteSpace: 'nowrap', zIndex: 3 }}>{praise}</div>
      )}
    </div>
  );
}


/* Slides that belong beside particular passages of a lesson.
   `after` is a phrase from the lesson text; the slide appears once that
   paragraph has been read. Phrases are matched case-insensitively, so light
   edits to the wording won't break the link. */
const SLIDE_IMG = (deck, n) => `/step-slides/${deck}-${String(n).padStart(2, '0')}.jpg`;
const LESSON_SLIDES = [
  { lesson: 'company', pins: [
    { after: '96% of contractors go out of business', deck: 'roof', n: 2, label: 'The problem with this industry' },
    { after: 'licensed in over 163 cities', deck: 'roof', n: 3, label: 'Licensed where you live — 163 cities' },
    { after: 'governing board is the CSLB', deck: 'roof', n: 4, label: 'Track record — CSLB, BBB, Edison' },
    { after: 'passed the five-year mark', deck: 'coat', n: 3, label: 'How much experience do you have?' },
    { after: 'coverage per household', deck: 'coat', n: 9, label: 'Excellent track record' },
    { after: 'repeat customers', deck: 'coat', n: 6, label: 'Repeat and referral business' },
    { after: 'production manager', deck: 'coat', n: 5, label: 'Who is assigned to my project?' },
  ]},
  { lesson: 'product demo', pins: [
    { after: 'explain all your options', deck: 'coat', n: 10, label: 'What options do you have?' },
    { after: 'First option is to do nothing', deck: 'coat', n: 11, label: 'Option 1 — Do nothing' },
    { after: 'Paint. These would be the second cheapest', deck: 'coat', n: 12, label: 'Option 2 — Paint' },
    { after: 'Complete restucco', deck: 'coat', n: 13, label: 'Option 3 — Re-stucco' },
    { after: 'called a color coat', deck: 'coat', n: 14, label: 'Option 4 — Color coat' },
    { after: 'vinyl siding', deck: 'coat', n: 15, label: 'Option 5 — Vinyl siding' },
    { after: 'Since 1961', deck: 'coat', n: 16, label: 'The ultimate option — COOLWALL®' },
    { after: 'McDonald', deck: 'coat', n: 17, label: 'Trusted by iconic brands' },
    { after: 'We trench approximately', deck: 'coat', n: 19, label: 'Step 1 — Trenching' },
    { after: 'We hydro blast', deck: 'coat', n: 20, label: 'Step 2 — Hydro-blast' },
    { after: 'THERMO BOND', deck: 'coat', n: 23, label: 'Step 4 — Recondition & repair' },
    { after: 'mask and tape off the entire home', deck: 'coat', n: 25, label: 'Step 6 — Masking & detail taping' },
    { after: 'we start with the primer coat', deck: 'coat', n: 27, label: 'Step 7 — The first coat of armor' },
  ]},
  { lesson: 'inspection', pins: [
    { after: 'What to Look For', deck: 'roof', n: 7, label: 'Your roof separates you from the elements' },
    { after: 'Wood Rot', deck: 'roof', n: 8, label: 'The damage — what to point out' },
  ]},
  { lesson: 'pricing', pins: [
    { after: 'Manufacturer\u2019s Suggested Retail Price', deck: 'coat', n: 30, label: 'What does the price include?' },
    { after: "Manufacturer's Suggested Retail Price", deck: 'coat', n: 30, label: 'What does the price include?' },
    { after: 'total of 25% off', deck: 'roof', n: 31, label: 'All-inclusive price policy' },
    { after: 'Tie-Down Question', deck: 'roof', n: 32, label: 'Financing options' },
  ]},
  { lesson: 'today', pins: [
    { after: 'quick recap', deck: 'coat', n: 31, label: 'TODAY — the five considerations' },
    { after: 'smart investment in', deck: 'roof', n: 33, label: 'TODAY — Company, Products, Protection, Representative, Investment' },
  ]},
  { lesson: 'intro', pins: [
    { after: 'here for our 10 o\u2019clock', deck: 'coat', n: 1, label: 'Opening slide' },
    { after: "here for our 10 o'clock", deck: 'coat', n: 1, label: 'Opening slide' },
  ]},
  { lesson: 'set the stage', pins: [
    { after: 'is it affordable', deck: 'coat', n: 31, label: 'The three questions, on screen' },
  ]},
];
function pinsFor(title) {
  const t = String(title || '').toLowerCase();
  const hit = LESSON_SLIDES.find(x => t.includes(x.lesson));
  return hit ? hit.pins : [];
}
/* Slides whose anchor phrase appears in this paragraph. */
function slidesAfter(para, pins, used) {
  const p = String(para || '').toLowerCase();
  const out = [];
  pins.forEach((pin, i) => {
    if (used.has(i)) return;
    const key = pin.deck + pin.n;
    if (used.has(key)) { used.add(i); return; }   // that slide is already on the page
    if (!p.includes(pin.after.toLowerCase())) return;
    used.add(i); used.add(key);
    out.push(pin);
  });
  return out;
}

/* A slide sitting in the flow of the lesson. */
function LessonSlide({ pin }) {
  const [big, setBig] = useState(false);
  return (
    <>
      <figure style={{ margin: '20px 0', padding: 0 }}>
        <button onClick={() => setBig(true)}
          style={{ display: 'block', width: '100%', padding: 0, border: '1px solid #dde0e5', borderRadius: 14,
            overflow: 'hidden', background: '#0b1320', cursor: 'zoom-in', boxShadow: '0 2px 12px rgba(15,36,29,.16)' }}>
          <img src={SLIDE_IMG(pin.deck, pin.n)} alt={pin.label} loading="lazy"
            style={{ width: '100%', display: 'block' }} />
        </button>
        <figcaption style={{ display: 'flex', gap: 9, alignItems: 'center', marginTop: 8, fontSize: 12, color: '#7c818a' }}>
          <span style={{ background: pin.deck === 'coat' ? '#F6EAD1' : '#eef0f4',
            color: pin.deck === 'coat' ? '#8A6B1F' : '#2f405b', fontWeight: 800, fontSize: 9.5,
            letterSpacing: '.06em', padding: '3px 8px', borderRadius: 999 }}>
            {pin.deck === 'coat' ? 'COATING' : 'ROOFING'} · SLIDE {pin.n}
          </span>
          <span>{pin.label}</span>
        </figcaption>
      </figure>
      {big && (
        <div onClick={() => setBig(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(6,15,12,.95)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, cursor: 'zoom-out' }}>
          <img src={SLIDE_IMG(pin.deck, pin.n)} alt={pin.label}
            style={{ maxWidth: '96vw', maxHeight: '88vh', borderRadius: 10 }} />
          <button onClick={() => setBig(false)} aria-label="Close"
            style={{ position: 'absolute', top: 18, right: 20, width: 44, height: 44, borderRadius: '50%',
              border: 'none', background: 'rgba(255,255,255,.16)', color: '#fff', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>
      )}
    </>
  );
}

// Wrap content so it fades/slides up the first time it scrolls into view.
function Reveal({ children, delay = 0 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { el.classList.add('revealed'); io.unobserve(el); } });
    }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <div ref={ref} className="reveal-on-scroll" style={{ animationDelay: delay + 'ms' }}>{children}</div>;
}

export default function TrainingLesson() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [lessonQuiz, setLessonQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCheat, setShowCheat] = useState(false);
  const [done, setDone] = useState(false);
  const [practiceOpen, setPracticeOpen] = useState(false);
  const [cardIdx, setCardIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [blankMode, setBlankMode] = useState(false);
  const [practiced, setPracticed] = useState(new Set());
  const [streakBump, setStreakBump] = useState(0);
  const markPracticed = (key) => { setPracticed(prev => { if (prev.has(key)) return prev; const n = new Set(prev); n.add(key); return n; }); setStreakBump(b => b + 1); };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  async function load() {
    setLoading(true);
    try { const d = await getLesson(id); setLesson(d.lesson); setLessonQuiz(d.lesson_quiz); }
    catch (e) { toast.error('Could not load lesson'); }
    finally { setLoading(false); }
  }

  async function markDone() {
    try { await completeLesson(id); setDone(true); toast.success('Lesson marked complete'); }
    catch (e) { toast.error('Could not save'); }
  }

  if (loading) return <div className="page-body"><div style={{ padding: 30, color: '#aaa' }}>Loading…</div></div>;
  if (!lesson) return <div className="page-body"><div style={{ padding: 30 }}>Lesson not found.</div></div>;

  const audioSrc = lesson.audio_filename ? getTrainingAudioUrl(lesson.audio_filename) : lesson.audio_url;

  // Build flashcards from the spoken scripts in this lesson (paragraphs/lines in quotes).
  const scripts = [];
  if (lesson.content) {
    let cue = lesson.title;
    lesson.content.split(/\n\n+/).forEach(para => {
      const m = para.match(/^([A-Z][A-Z0-9 &/()'-]{2,}?)(:|\s—|\s-)\s*([\s\S]*)$/);
      if (m && m[1].length < 60) cue = m[1].trim().replace(/[:]+$/, '');
      const t = para.trim();
      if (/^["“]/.test(t)) scripts.push({ cue, text: t });
      else para.split(/\n/).forEach(line => {
        const l = line.trim();
        if (/^["“]/.test(l) && l.length > 12) scripts.push({ cue, text: l });
      });
    });
  }
  const card = scripts[cardIdx] || null;
  const nextCard = () => { setRevealed(false); setCardIdx(i => (i + 1) % scripts.length); };
  const prevCard = () => { setRevealed(false); setCardIdx(i => (i - 1 + scripts.length) % scripts.length); };

  // Render a block of text line-by-line with colorful cues — never changes the words.
  function renderBody(text, key, leadIn) {
    // 1) Merge lines that were hard-wrapped mid-sentence into single logical blocks,
    //    so one sentence is never split across two boxes.
    const raw = text.split(/\n/).map(l => l.trim()).filter(Boolean);
    const blocks = [];
    for (const line of raw) {
      const isBullet = /^[•\-]\s+/.test(line) || /^\d+[.)]\s+/.test(line);
      const isQuote = /^["“]/.test(line);
      const prev = blocks[blocks.length - 1];
      // a previous block keeps absorbing lines until it ends with sentence punctuation
      const prevOpen = prev && !prev.bullet && !prev.quote && !/[.!?:"”]$/.test(prev.text);
      if (!isBullet && !isQuote && prevOpen) {
        prev.text += ' ' + line;
      } else {
        blocks.push({ text: line, bullet: isBullet, quote: isQuote });
      }
    }

    return (
      <div style={{ margin: '8px 0 14px' }}>
        {blocks.map((b, j) => {
          const line = b.text;
          if (b.quote) {
            const prevLine = j > 0 ? blocks[j - 1].text : (leadIn || '');
            if (isExampleCue(prevLine)) {
              return <ExampleQuote key={j} text={line} small />;
            }
            return <SayBox key={j} text={line} small onPracticed={() => markPracticed(key + '-' + j)} />;
          }
          if (b.bullet) {
            const txt = line.replace(/^[•\-]\s+/, '').replace(/^\d+[.)]\s+/, '');
            const num = /^\d/.test(line) ? line.match(/^(\d+)/)[1] : null;
            return (
              <div key={j} className="row-slide" style={{ display: 'flex', gap: 10, alignItems: 'flex-start', margin: '6px 0' }}>
                <span style={{ flexShrink: 0, marginTop: 1, width: 22, height: 22, borderRadius: '50%', background: num ? '#0d1f3b' : '#e7ebf1', color: num ? '#fff' : '#1a355f', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>{num || '✓'}</span>
                <span>{txt}</span>
              </div>
            );
          }
          const upper = line.toUpperCase();
          const isQuestion = /\?$/.test(line);
          // blue-pill / red-pill thematic pair — always highlight both
          if (/blue pill/i.test(line)) {
            return <div key={j} className="row-slide" style={{ background: '#e3f0fb', borderRadius: 8, padding: '10px 14px', margin: '8px 0', color: '#0d4a73', borderLeft: '4px solid #2196f3' }}><b className="icon-pop">💊</b> {line}</div>;
          }
          if (/red pill/i.test(line)) {
            return <div key={j} className="row-slide" style={{ background: '#fdecea', borderRadius: 8, padding: '10px 14px', margin: '8px 0', color: '#9a2620', borderLeft: '4px solid #e53935' }}><b className="icon-pop">💊</b> {line}</div>;
          }
          // Only flag as a WARNING when the line *begins* with an imperative (not mid-sentence,
          // not a question). This kills the false reds on narration like "...objections don't mean...".
          const isWarn = !isQuestion && /^(DON['’]?T|DO NOT|NEVER|AVOID|STOP)\b/.test(upper);
          const isDo = !isQuestion && !isWarn && /^(ALWAYS|DO)\b/.test(upper) && !/^DO (YOU|WE|I|THEY|YOUR|NOT)\b/.test(upper);
          if (isWarn) {
            return <div key={j} className="row-slide" style={{ background: '#fdecea', borderRadius: 8, padding: '8px 12px', margin: '7px 0', color: '#9a2620', borderLeft: '4px solid #e57373' }}><b className="icon-pop">⛔</b> {line}</div>;
          }
          if (isDo) {
            return <div key={j} className="row-slide" style={{ background: '#eaeff6', borderRadius: 8, padding: '8px 12px', margin: '7px 0', color: '#1a355f', borderLeft: '4px solid #6586bc' }}><b className="icon-pop">✅</b> {line}</div>;
          }
          return <p key={j} style={{ margin: '8px 0' }}>{line}</p>;
        })}
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <CprLogo height={34} style={{ flexShrink: 0, maxWidth: 200 }} />
          <h2 style={{ margin: 0 }}>{lesson.title}</h2>
        </div>
        <button className="btn btn-outline btn-sm" onClick={() => navigate('/training')}>← Back to Training</button>
      </div>
      <div className="page-body">
        <div className="lesson-layout">
        <div className="lesson-main" style={{ background: '#fff', borderRadius: 14, padding: 24, border: '1px solid #eee' }}>
          {(() => {
            // count only rep scripts (quotes that are NOT customer-example quotes)
            const lines = (lesson.content || '').split(/\n/).map(s => s.trim());
            let total = 0;
            for (let li = 0; li < lines.length; li++) {
              if (/^["\u201c]/.test(lines[li])) {
                let prev = ''; for (let k = li - 1; k >= 0; k--) { if (lines[k]) { prev = lines[k]; break; } }
                if (!isExampleCue(prev)) total++;
              }
            }
            if (!total) return null; const n = practiced.size; const all = n >= total; return (
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: all ? 'linear-gradient(135deg,#1a355f,#2d4c7e)' : '#fff8e1', border: '1px solid ' + (all ? '#1a355f' : '#ffe082'), borderRadius: 12, padding: '10px 16px', color: all ? '#fff' : '#7a5d00' }}>
                <div style={{ fontWeight: 800, fontSize: 14 }}><span key={streakBump} className="streak-bump">{all ? '\ud83c\udfc6' : '\ud83d\udd25'}</span> {all ? 'All scripts practiced \u2014 you\u2019re ready!' : `${n} of ${total} scripts practiced`}</div>
                <div style={{ display: 'flex', gap: 4 }}>{Array.from({ length: total }).map((_, k) => <span key={k} style={{ width: 22, height: 6, borderRadius: 3, background: k < n ? (all ? '#fff' : '#c9a227') : 'rgba(0,0,0,0.12)', transition: 'background .3s' }} />)}</div>
              </div>
              {n === 0 && <div style={{ fontSize: 12.5, color: '#777', marginTop: 7, lineHeight: 1.5 }}>💡 The green boxes below are word-for-word scripts. <b>Tap one</b> to hide some words, recite the full line <b>out loud</b> from memory, then <b>tap again</b> to reveal it and check yourself. Practice all {total} to fill the bar.</div>}
            </div>
          ); })()}
          <LessonHero title={lesson.title} lessonId={id} />
          {lesson.cheatsheet && (
            <button onClick={() => setShowCheat(s => !s)} style={{ marginBottom: 16, background: '#fff3cd', color: '#8a6d00', border: '1px solid #ffe082', borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
              ⚡ {showCheat ? 'Hide' : 'Quick'} cheat-sheet
            </button>
          )}
          {showCheat && lesson.cheatsheet && (
            <div style={{ background: '#fffbe6', border: '1px solid #ffe082', borderRadius: 10, padding: 16, marginBottom: 18, whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.6 }}>
              {lesson.cheatsheet}
            </div>
          )}

          {audioSrc && (
            <div style={{ marginBottom: 20 }}>
              {lesson.audio_url && !lesson.audio_filename && /youtube|youtu\.be|vimeo/.test(lesson.audio_url) ? (
                <a href={lesson.audio_url} target="_blank" rel="noreferrer" className="btn btn-primary">▶ Open audio/video lesson</a>
              ) : (
                <audio controls style={{ width: '100%' }} src={audioSrc}>Your browser does not support audio.</audio>
              )}
            </div>
          )}

          {lesson.content && (
            <div style={{ fontSize: 15, lineHeight: 1.7, color: '#333' }}>
              {(() => {
                const paras = lesson.content.split(/\n\n+/);
                const pins = pinsFor(lesson.title || '');
                const usedPins = new Set();
                return paras.map((para, i) => {
                  const here = pins.length ? slidesAfter(para, pins, usedPins) : [];
                  const withSlides = (node) => here.length
                    ? <div key={i}>{node}{here.map((pin, k) => <LessonSlide key={'s' + k} pin={pin} />)}</div>
                    : node;
                  const t = para.trim();
                  const prevPara = i > 0 ? paras[i - 1].trim() : '';
                  // last non-empty line of the previous paragraph = the lead-in cue
                  const prevLines = prevPara.split(/\n/).map(s => s.trim()).filter(Boolean);
                  const leadIn = prevLines.length ? prevLines[prevLines.length - 1] : '';
                  const m = para.match(/^([A-Z][A-Z0-9 &/()'-]{2,}?)(:|\s—|\s-)\s*([\s\S]*)$/);
                  const isScript = /^["“]/.test(t);

                  // Spoken quote that stands alone
                  if (isScript) {
                    if (isExampleCue(leadIn)) {
                      return withSlides(<Reveal key={i}><ExampleQuote text={para} /></Reveal>);
                    }
                    return withSlides(<Reveal key={i}><SayBox text={para} onPracticed={() => markPracticed('p' + i)} /></Reveal>);
                  }

                  // Section header with illustration
                  if (m && m[1].length < 60) {
                    const rest = m[3];
                    return withSlides(
                      <Reveal key={i}>
                        <LessonArt header={m[1]} />
                        {rest && renderBody(rest, i, m[1])}
                      </Reveal>
                    );
                  }

                  return withSlides(<Reveal key={i}>{renderBody(para, i, leadIn)}</Reveal>);
                });
              })()}
            </div>
          )}

          <div style={{ marginTop: 24, paddingTop: 18, borderTop: '1px solid #eee', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={markDone} disabled={done}>{done ? '✓ Completed' : 'Mark as complete'}</button>
            {scripts.length > 0 && <button className="btn btn-outline" onClick={() => { setCardIdx(0); setRevealed(false); setBlankMode(false); setPracticeOpen(true); }}>🎤 Practice the scripts ({scripts.length})</button>}
            {lessonQuiz && <button className="btn btn-outline" onClick={() => navigate(`/training/quiz/${lessonQuiz.id}`)}>Take lesson quiz →</button>}
          </div>
        </div>
        <PracticeArena content={lesson.content} lessonId={id} />
        </div>
      </div>

      {practiceOpen && card && (
        <div onClick={() => setPracticeOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,30,22,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 560, background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' }}>
            <div style={{ background: 'linear-gradient(135deg,#0d1f3b,#152e55)', color: '#fff', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <RepMascot pose="ok" height={50} style={{ marginBottom: -16, marginTop: -8 }} />
                <div style={{ fontWeight: 800 }}>🎤 Practice Scripts</div>
              </div>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                <span style={{ fontSize: 13, opacity: 0.9 }}>{cardIdx + 1} / {scripts.length}</span>
                <span onClick={() => setPracticeOpen(false)} style={{ cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</span>
              </div>
            </div>
            <div style={{ padding: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#c9a227', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 6 }}>{card.cue}</div>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 14 }}>{blankMode ? 'Fill in the blanks out loud, then reveal.' : revealed ? 'Here\'s the script — did you nail it?' : 'Say it from memory, then reveal.'}</div>

              <div style={{ minHeight: 130, borderRadius: 12, border: '2px dashed ' + (revealed ? '#1a355f' : '#d4d7dd'), background: revealed ? '#eaeff6' : '#f7f8f9', padding: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: 16, fontStyle: 'italic', color: revealed ? '#14223a' : '#9a9fa6', cursor: 'pointer' }}
                onClick={() => setRevealed(true)}>
                {revealed ? card.text : blankMode ? blankWords(card.text) : '🤔  Tap to reveal'}
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={{ fontSize: 13, color: '#555', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={blankMode} onChange={e => { setBlankMode(e.target.checked); setRevealed(false); }} />
                  Fill-in-the-blank
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-outline btn-sm" onClick={prevCard}>← Prev</button>
                  {!revealed && <button className="btn btn-primary btn-sm" onClick={() => setRevealed(true)}>Reveal</button>}
                  <button className="btn btn-primary btn-sm" onClick={nextCard}>Next →</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
